/** Help panel keyboard-shortcuts section with core/more tier tables. */
import { useId, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  APP_KEYBOARD_SHORTCUT_GROUPS,
} from '@/constants/keyboardShortcuts';
import {
  getGoBarKeysI18nKey,
  getShortcutsForHelpTier,
} from '@/components/layout/helpShortcutTiers';

const helpTableSx = {
  tableLayout: 'fixed',
  width: '100%',
  '& .MuiTableCell-root': {
    wordBreak: 'break-word',
    verticalAlign: 'top',
    px: 1,
    py: 0.75,
  },
};

const monospaceCellSx = { fontFamily: 'monospace', fontWeight: 600 };

function resolveShortcutKeysLabel(t, shortcut) {
  if (shortcut.id === 'goBar') {
    return t(getGoBarKeysI18nKey());
  }
  return t(shortcut.keysKey);
}

/**
 * @param {(key: string) => string} t
 * @param {import('@/constants/keyboardShortcuts').AppKeyboardShortcutGroup} group
 * @param {import('@/constants/keyboardShortcuts').AppKeyboardShortcut[]} shortcuts
 * @param {string} [keySuffix]
 */
function renderShortcutGroupTable(t, group, shortcuts, keySuffix = '') {
  if (shortcuts.length === 0) return null;

  const groupTitleKey = `help.shortcuts.group.${group}`;
  const tableLabel = `${t('help.shortcuts.title')} — ${t(groupTitleKey)}`;

  return (
    <Box key={`${group}${keySuffix}`} sx={{ mb: 2 }}>
      <Typography
        variant="subtitle2"
        component="h4"
        sx={{ fontWeight: 600, mb: 0.75 }}
      >
        {t(groupTitleKey)}
      </Typography>
      <Table size="small" sx={helpTableSx} aria-label={tableLabel}>
        <TableHead>
          <TableRow>
            <TableCell component="th" scope="col" sx={{ width: '38%' }}>
              {t('help.shortcuts.col.key')}
            </TableCell>
            <TableCell component="th" scope="col">
              {t('help.shortcuts.col.action')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shortcuts.map((shortcut) => (
            <TableRow key={shortcut.id}>
              <TableCell sx={monospaceCellSx}>
                {resolveShortcutKeysLabel(t, shortcut)}
              </TableCell>
              <TableCell>{t(shortcut.descriptionKey)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}

/** @param {'core' | 'more'} tier */
function renderTierGroupTables(t, tier) {
  const tierShortcuts = getShortcutsForHelpTier(tier);
  return APP_KEYBOARD_SHORTCUT_GROUPS.map((group) => {
    const shortcuts = tierShortcuts.filter((s) => s.group === group);
    return renderShortcutGroupTable(t, group, shortcuts, `-${tier}`);
  });
}

/**
 * @param {{ t: (key: string) => string }} props
 */
export function HelpShortcutsSection({ t }) {
  const [moreOpen, setMoreOpen] = useState(false);
  const morePanelId = useId();

  return (
    <Box component="section" sx={{ mb: 3 }}>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600, mb: 0.5 }}>
        {t('help.shortcuts.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {t('help.shortcuts.intro')}
      </Typography>
      {renderTierGroupTables(t, 'core')}
      <Button
        type="button"
        onClick={() => setMoreOpen((open) => !open)}
        aria-expanded={moreOpen}
        aria-controls={morePanelId}
        sx={{ mb: moreOpen ? 1 : 0, px: 0, minWidth: 0, textTransform: 'none' }}
      >
        {t('help.shortcuts.more')}
      </Button>
      <Collapse in={moreOpen} timeout="auto" unmountOnExit>
        <Box id={morePanelId}>
          {renderTierGroupTables(t, 'more')}
          {/* Footnote covers indent/outdent — those live in the collapsed "more" tier. */}
          <Typography variant="caption" color="text.secondary" component="p">
            {t('help.shortcuts.footnote.structuralNav')}
          </Typography>
        </Box>
      </Collapse>
    </Box>
  );
}
